import { FC, useMemo } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { GameType, Reward, RewardType } from 'ui/types/Games';
import { formatNumber, getOrdinal, pluralize } from 'ui/helpers/numbers';
import Coins from 'ui/assets/Coins';
import RaffleTicket from 'ui/assets/RaffleTicket';
interface CbGamePrizeProps {
  isShowingJoinable?: boolean;
  gameType: GameType;
  maxPrizesToShow?: number;
  rewards: Reward[];
}
export const CbGamePrize: FC<CbGamePrizeProps> = ({
  isShowingJoinable = false,
  gameType,
  rewards,
  maxPrizesToShow = null
}) => {
  const renderPrizeRank = (prizeIndex: number, renderingTickets: boolean) => <Flex alignItems={'center'} backgroundColor="luigisPants.600" borderRadius={100} color="white" fontFamily={'codenext'} fontSize="sm" height={'35px'} justifyContent={'center'}
  /* 
    When we render tickets the text is 2 x <Ticket Icon>
    But for tokens it's just 200 <Coin Icon>
    The presence of the x (for the multiplication symbol) means we need slightly less padding
  */ marginRight={renderingTickets ? 2 : 4} padding={2} width={'35px'} data-sentry-element="Flex" data-sentry-component="renderPrizeRank" data-sentry-source-file="CbGamePrize.tsx">
      <Text fontSize={'xs'} data-sentry-element="Text" data-sentry-source-file="CbGamePrize.tsx">{getOrdinal(prizeIndex + 1)}</Text>
    </Flex>;
  const hasHiddenRewards = !!maxPrizesToShow && rewards.length > maxPrizesToShow;
  const rewardsToRender = useMemo(() => {
    if (maxPrizesToShow && hasHiddenRewards) {
      return rewards.slice(0, maxPrizesToShow);
    }
    return rewards;
  }, [rewards, maxPrizesToShow]);
  const renderJackpotInfo = () => {
    const {
      jackpot,
      jackpotCount,
      jackpotWinMaximum
    } = rewards[0];
    const shouldShowJackpotWinLimit = Number(jackpotWinMaximum) > 0 && jackpotWinMaximum != jackpotCount;
    return <>
        <Text color="cloud.500" fontSize="xs" fontWeight="bold" marginTop={2} textAlign={'center'} data-sentry-element="Text" data-sentry-source-file="CbGamePrize.tsx">
          {!!isShowingJoinable ? 'Available Game' : ''}{' '}
          {pluralize(jackpotCount || 1, 'Jackpot').toUpperCase()}
        </Text>
        <Flex width="100%" justifyContent={rewardsToRender.length > 3 ? 'space-between' : 'space-evenly'} marginY={2} flexWrap={'wrap'} paddingX={2} flexDirection={{
        base: 'column',
        md: 'row'
      }} data-sentry-element="Flex" data-sentry-source-file="CbGamePrize.tsx">
          <Flex alignItems={'center'} marginBottom={2} data-sentry-element="Flex" data-sentry-source-file="CbGamePrize.tsx">
            <Coins width={9} height="auto" marginRight={2} data-sentry-element="Coins" data-sentry-source-file="CbGamePrize.tsx" />
            <Text color="white" fontWeight={'bold'} marginRight={2} data-sentry-element="Text" data-sentry-source-file="CbGamePrize.tsx">
              {formatNumber(Number(jackpot))}
            </Text>
            <Text color="white" fontWeight={'semibold'} fontSize={'sm'} data-sentry-element="Text" data-sentry-source-file="CbGamePrize.tsx">
              x {jackpotCount}
            </Text>
            {!!shouldShowJackpotWinLimit && <Text color="white" marginLeft={0.5} fontWeight={'semibold'} fontSize={'xs'}>
                {`(Max ${jackpotWinMaximum} per learner)`}
              </Text>}
          </Flex>
        </Flex>
      </>;
  };
  const renderTicketInfo = () => {
    return <>
        <Text color="cloud.500" fontSize="xs" fontWeight="bold" marginTop={2} textAlign={'center'} data-sentry-element="Text" data-sentry-source-file="CbGamePrize.tsx">
          {!!isShowingJoinable ? 'Available Game' : ''}{' '}
          {pluralize(rewards.length, 'Ticket').toUpperCase()}
        </Text>
        <Flex width="100%" justifyContent={rewardsToRender.length > 3 ? 'space-between' : 'space-evenly'} marginY={2} flexWrap={'wrap'} paddingX={2} flexDirection={{
        base: 'column',
        md: 'row'
      }} data-sentry-element="Flex" data-sentry-source-file="CbGamePrize.tsx">
          {rewardsToRender.map((reward, index) => <Flex alignItems={'center'} key={index}>
              {gameType !== GameType.Incentive && renderPrizeRank(index, true)}
              <RaffleTicket width={9} height="auto" marginRight={2} />
              <Text color="white" fontWeight={'semibold'} fontSize={'sm'}>
                {`x ${formatNumber(Number(reward.value))}`}
              </Text>
            </Flex>)}
        </Flex>
      </>;
  };
  const renderTokenInfo = () => {
    return <>
        <Text color="cloud.500" fontSize="xs" fontWeight="bold" marginTop={2} textAlign={'center'} data-sentry-element="Text" data-sentry-source-file="CbGamePrize.tsx">
          {!!isShowingJoinable ? 'Available Game' : ''}{' '}
          {pluralize(rewards.length, 'Prize').toUpperCase()}
        </Text>
        <Flex width="100%" justifyContent={rewardsToRender.length > 3 ? 'space-between' : 'space-evenly'} marginY={2} flexWrap={'wrap'} paddingX={2} flexDirection={{
        base: 'column',
        md: 'row'
      }} data-sentry-element="Flex" data-sentry-source-file="CbGamePrize.tsx">
          {rewardsToRender.map((reward, index) => <Flex alignItems={'center'} marginBottom={2} key={index}>
              {gameType !== GameType.Incentive && renderPrizeRank(index, false)}
              <Coins width={9} height="auto" marginRight={2} />
              <Text color="white" fontWeight={'bold'}>
                {formatNumber(Number(reward.value))}
              </Text>
            </Flex>)}
        </Flex>
      </>;
  };
  const renderGiftcardInfo = () => {
    return <>
        <Text color="cloud.500" fontSize="xs" fontWeight="bold" marginTop={2} textAlign={'center'} data-sentry-element="Text" data-sentry-source-file="CbGamePrize.tsx">
          {!!isShowingJoinable ? 'Available Game' : ''}{' '}
          {pluralize(rewards.length, 'Prize').toUpperCase()}
        </Text>
        <Flex width="100%" justifyContent={rewardsToRender.length > 3 ? 'space-between' : 'space-evenly'} marginY={2} flexWrap={'wrap'} paddingX={2} flexDirection={{
        base: 'column',
        md: 'row'
      }} data-sentry-element="Flex" data-sentry-source-file="CbGamePrize.tsx">
          {rewardsToRender.map((reward, index) => <Flex alignItems={'center'} justifyContent={'center'} marginBottom={2} key={index}>
              {gameType !== GameType.Incentive && renderPrizeRank(index, false)}
              <Box h={'32px'} w={'48px'} backgroundImage={`url("${reward.giftCardImage}")`} backgroundRepeat={'no-repeat'} backgroundSize={'cover'} marginRight={2} />
              <Text color="white" fontWeight={'bold'}>
                ${formatNumber(Number(reward.value))} {reward.giftCardName}
              </Text>
            </Flex>)}
        </Flex>
      </>;
  };
  const isJackpotGame = rewards[0]?.type === RewardType.Jackpot;
  const isGiftCardReward = rewards[0]?.type === RewardType.GiftCard;
  const isTokenGame = rewards[0]?.type === RewardType.Tokens;
  return <Flex alignItems={'center'} backgroundColor={'#12275A'} borderRadius={16} direction="column" marginTop={4} data-sentry-element="Flex" data-sentry-component="CbGamePrize" data-sentry-source-file="CbGamePrize.tsx">
      {!!isJackpotGame && renderJackpotInfo()}
      {!!isJackpotGame && renderTicketInfo()}
      {isTokenGame && renderTokenInfo()}
      {isGiftCardReward && renderGiftcardInfo()}
      {hasHiddenRewards && <Text color="cloud.500" fontSize="xs" fontWeight="bold" textAlign={'center'}>
          {`+${rewards.length - maxPrizesToShow} more`}
        </Text>}
    </Flex>;
};